/* @refresh skip */
// IE Hub - Route Configuration
// Main routing configuration for the application

import { lazy } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import CheckColor from '@/pages/CheckColor';
import AuthScreen from '@/pages/public/authScreen';
import NewOnBoardingScreen from '@/pages/private/onBoarding/newOnBoardingScreen';

import RequireAuth from './guards/RequireAuth';
import GuestOnly from './guards/GuestOnly';
import BudgetPage from '@/pages/private/dashboard/budgetPage';
import SharePage from '@/pages/private/dashboard/SharePage';
import UpdateBudgetPage from '@/pages/private/noNavButFooter/updateBudgetPage';
import MonthlyBudgetSummary from '@/pages/private/noNavButFooter/montlyBudgetSummary';
import DocumentPage from '@/pages/private/dashboard/DocumentPage';
import VerifyAddressGuard from './guards/VerifyAddressGuard';
import RequireBudgetOnboarding from './guards/RequireBudgetOnboarding';
import ProfilePage from '@/pages/private/fromDashboard/profilePage';
import HelpAndSupportPage from '@/pages/private/fromDashboard/HelpAndSupportPage';
// FaqPage kept in codebase but route now redirects to SupportAndFaqsPage
// import FaqPage from '@/pages/private/fromDashboard/faqPage';
import SupportAndFaqsPage from '@/pages/private/fromDashboard/SupportAndFaqsPage';
import SupportAndFaqsPublic from '@/pages/public/SupportAndFaqsPublic';
import LenderConsentScreen from '@/pages/public/LenderConsentScreen';
import OpenBankingPage from '@/pages/private/fromDashboard/OpenBankingPage';
import ObConfirmTransactionPage from '@/pages/private/fromDashboard/obConfirmTransactionPage';
import ObSummaryPage from '@/pages/private/fromDashboard/obSummary';
import ObThankYou from '@/pages/private/fromDashboard/obThankYou';
import ShareSuccessPage from '@/pages/private/fromDashboard/ShareSuccessPage';
import BenefitsCheckPage from '@/pages/private/fromDashboard/BenefitsCheckPage';
import FreePostPage from '@/pages/private/fromDashboard/freePostPage';
import AddCompanyPage from '@/pages/private/fromDashboard/addCompanyPage';

// Lazy load route components
const Dashboard = lazy(() => import('../../pages/private/dashboard/Dashboard'));
const ScreenRoute = lazy(() => import('./ScreenRoute'));
const NotFound = lazy(() => import('../../pages/public/NotFound'));
const NotificationTools = lazy(() => import('../../NotificationTools'));
const JSONScreenDemo = lazy(() => import('./JSONScreenDemo'));
const OnBoarding = lazy(() => import('../../pages/OnBoarding'));
const GoogleCallback = lazy(() => import('./auth/google-callback'));
const VerifyEmail = lazy(() => import('./auth/verify-email.'));
const AcceptTerms = lazy(() => import('../../pages/AcceptTerms'));
const RootRoute = lazy(() => import('./RootRoute'));

function SignupRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/auth${search}`} replace />;
}

// Route configuration
export const routes = [
  // Root route (handles scode parameter)
  {
    path: '/',
    element: <RootRoute />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },

  // Private routes (require authentication)
  {
    element: <RequireAuth />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>,
    children: [
      // Dashboard explicit path
   
      // Onboarding routes
      {
        path: 'onboarding',
        element: <OnBoarding />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'new-onboarding',
        element: <NewOnBoardingScreen />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
  
      // Restrict onboarding guard to budget, documents, and share pages
      {
        element: <RequireBudgetOnboarding />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>,
        children: [
          {
            path: 'budget',
            element: <BudgetPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'documents',
            element: <DocumentPage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
          {
            path: 'share',
            element: <SharePage />,
            errorElement: <ErrorBoundary><div /></ErrorBoundary>
          },
        ]
      },
      // Routes outside the onboarding guard
      {
        path: 'dashboard',
        element: <Dashboard />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },

      {
        path: 'share-success',
        element: <ShareSuccessPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'freepost',
        element: <FreePostPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'addcompany',
        element: <AddCompanyPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'profile',
        element: <ProfilePage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'help-and-support',
        element: <HelpAndSupportPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'support-faqs',
        element: <SupportAndFaqsPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'faqs',
        element: <Navigate to="/support-faqs" replace />,
      },
      {
        path: 'open-banking',
        element: <OpenBankingPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'obq',
        element: <ObConfirmTransactionPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'ob-confirm-transaction',
        element: <ObConfirmTransactionPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'ob-summary',
        element: <ObSummaryPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'ob-thank-you',
        element: <ObThankYou />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'benefits',
        element: <BenefitsCheckPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      
      {
        path: 'update-budget',
        element: <UpdateBudgetPage />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'monthly-budget-summary',
        element: <MonthlyBudgetSummary />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'accept-terms',
        element: <AcceptTerms />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'verify-address',
        element: <VerifyAddressGuard />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      // Dynamic screen routes
      {
        path: 'screen/:schemaId',
        element: <ScreenRoute />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      // Tools and demos
      {
        path: 'notifications',
        element: <NotificationTools />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'demo',
        element: <JSONScreenDemo />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
      {
        path: 'color-check',
        element: <CheckColor />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>
      },
    ]
  },

  // Guest routes (public access)
  {
    path: 'auth',
    element: <GuestOnly><AuthScreen /></GuestOnly>,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },
  {
    path: 'auth/google-callback',
    element: <GoogleCallback />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },
  {
    path: 'auth/verify-email',
    element: <VerifyEmail />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },

  // Public Support & FAQ's page (no auth required, embeddable via iframe)
  {
    path: 'help',
    element: <SupportAndFaqsPublic />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },

  // Public Lender Consent Flow (no auth required)
  {
    path: 'lender-consent',
    element: <LenderConsentScreen />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },
  {
    path: 'share-consent',
    element: <LenderConsentScreen />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },
  {
    path: 'consent',
    element: <LenderConsentScreen />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },

  // Legacy route support: old /#/signup should land on /#/auth (preserve query)
  {
    path: 'signup',
    element: <SignupRedirect />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  },

  // Catch-all for 404
  {
    path: '*',
    element: <NotFound />,
    errorElement: <ErrorBoundary><div /></ErrorBoundary>
  }
];

export default routes;